




















































































































































































































































































































































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import draggable from "vuedraggable";
import WorkspaceOptions from "@/store/modules/WorkspaceOptions";
import ConfirmationDialog from "@/components/common/ConfirmationDialog.vue";
import SimpleMenuTextEditor from "@/components/common/SimpleMenuTextEditor.vue";
import { NotificationEventBus } from "@/EventBus";
import * as rules from "@/rules";
import _ from "lodash";
import FactorScoringDefinitionCreator from "@/components/scoring-definition/FactorScoringDefinitionCreator.vue";
import ScoringKeys, { ScoringKeyDefinition } from "@/store/modules/ScoringKeys";
import ValueTypes from "@/utils/ValueTypes";
import {
    Factor,
    FactorGroup,
    FactorResource,
    ValueTypeDefinition,
} from "@/api/model/FactorResource";
import Modelling from "@/store/modules/Modelling";
import Decisions from "@/store/modules/Decisions";
import FactorGroupScoringDefinition from "@/components/scoring-definition/FactorGroupScoringDefinition.vue";
import FixedTableAdditionalMenu from "@/views/ModelBuilder/FixedTableAdditionalMenu.vue";

const decisionsModule = getModule(Decisions);
const modellingModule = getModule(Modelling);
const workspaceOptionsModule = getModule(WorkspaceOptions);

@Component({
    components: {
        FactorScoringDefinitionCreator,
        SimpleMenuTextEditor,
        ConfirmationDialog,
        draggable,
        FactorGroupScoringDefinition,
        FixedTableAdditionalMenu,
    },
})
export default class ModelFixedTableBuilder extends Vue {
    private readonly SUMMARY_SCORING_FUNCTIONS = [
        {
            text: "Weighted Sum",
            help:
                "For factors within this group, the score will be calculated using the average of child factor scores",
            value: "WEIGHTED_SUM",
        },
        {
            text: "Min",
            help:
                "The score for this section will be the factor within the section with the lowest score",
            value: "MIN",
        },
        {
            text: "Max",
            help:
                "The score for this section will be the factor within the section with the lowest score",
            value: "MAX",
        },
    ];

    private rules = rules;

    private readonly VALUE_TYPES = ValueTypes.VALUE_TYPES;
    private readonly TEXT_FORMATS = ValueTypes.TEXT_FORMATS;
    private readonly DATE_TIME_FORMATS = ValueTypes.DATE_TIME_FORMATS;
    private readonly CATEGORY_FORMATS = ValueTypes.CATEGORY_FORMATS;

    @Prop()
    tableFactorResource: FactorGroup;

    @Ref()
    private createFormRef: HTMLFormElement;

    private createDialog = false;
    private createForm: {
        name?: string;
        type: "ROW" | "COLUMN";
        summaryScoringFunction: "WEIGHTED_SUM" | "MIN" | "MAX";
        valueTypeInformation?: any;
        valuePrefix?: string;
        valueSuffix?: string;
        categoricalValues: string[];
        format: string;
    } = {
        type: "ROW",
        summaryScoringFunction: "WEIGHTED_SUM",
        categoricalValues: [],
        format: "",
    };

    private creating = false;

    get selectedDecision() {
        return decisionsModule.selectedDecision;
    }

    get factors() {
        return modellingModule.workingModelFactorResources;
    }

    get childFactorResources() {
        return this.tableFactorResource.childrenIds
            .map((cId) => this.factors[cId])
            .filter((f) => f);
    }

    get columns(): FactorGroup[] {
        return (this.childFactorResources as FactorGroup[]).filter(
            (f) => f.groupTypeDefinition && f.groupTypeDefinition.type === "TABLE_COLUMN"
        );
    }

    // This is used for the draggable component so that we get and update only the columns
    get columnIds() {
        return this.columns.map((c) => c.id);
    }

    // We leverage this setter to update the childrenIds in the main table for reordering columns and rows.
    // Without this, it does not work because draggable tries to mess around with both the columns and rows since
    // the childrenIds contains both. This note applies to set rowIds() as well.
    set columnIds(val: string[]) {
        Vue.set(this.tableFactorResource, "childrenIds", [...val, ...this.rowIds]);
    }

    get rows() {
        return (this.childFactorResources as FactorGroup[]).filter(
            (f) => f.groupTypeDefinition && f.groupTypeDefinition.type === "TABLE_ROW"
        );
    }

    get rowIds() {
        return this.rows.map((c) => c.id);
    }

    set rowIds(val: string[]) {
        Vue.set(this.tableFactorResource, "childrenIds", [...this.columnIds, ...val]);
    }

    get tableGrid() {
        let grid: any = {};
        this.columns.forEach((c) => {
            grid[c.id] = {};
            this.rows.forEach((r) => {
                let factorIds = _.intersection(c.childrenIds, r.childrenIds);
                if (factorIds.length === 1) {
                    grid[c.id][r.id] = factorIds[0];
                }
            });
        });
        return grid;
    }

    get editModel() {
        return workspaceOptionsModule.editModel;
    }

    get editSumRule() {
        return workspaceOptionsModule.editSumRule;
    }

    get editScoringRule() {
        return workspaceOptionsModule.editScoringRule;
    }

    @Watch("createForm.valueTypeInformation.valueType")
    private updateFormat() {
        if (this.createForm.valueTypeInformation?.display === "CATEGORICAL") {
            this.createForm.format = "DROP_DOWN";
        } else if (this.createForm.valueTypeInformation?.display === "DATE AND TIME") {
            this.createForm.format = "YYYY-MM-DD HH:mm:ss";
        }
    }
    //Because the children contain both rows and columns, we need to set the new value to trigger the update.
    private async columnsMoved(e: any) {
        this.columnIds = _.cloneDeep(this.columnIds);
        await modellingModule.updateFactorResource(this.tableFactorResource);
    }

    private async rowsMoved(e: any) {
        this.rowIds = _.cloneDeep(this.rowIds);
        await modellingModule.updateFactorResource(this.tableFactorResource);
    }

    private get isRowsCustom() {
        let groupTypeDefinitions = this.columns
            .map((fg: any) => fg.groupTypeDefinition.valueTypeDefinition)
            .filter((gtd: any) => gtd != null);
        // console.log(groupTypeDefinitions);
        return groupTypeDefinitions.length === 0;
    }

    private get isColumnsCustom() {
        let groupTypeDefinitions = this.rows
            .map((fg: any) => fg.groupTypeDefinition.valueTypeDefinition)
            .filter((gtd: any) => gtd != null);
        // console.log(groupTypeDefinitions);
        return groupTypeDefinitions.length === 0;
    }

    private async deleteColumn(columnId: string) {
        let column = this.factors[columnId] as FactorGroup;
        await modellingModule.deleteFactorResources([columnId, ...column.childrenIds]);
    }

    private async deleteRow(rowId: string) {
        let row = this.factors[rowId] as FactorGroup;
        await modellingModule.deleteFactorResources([rowId, ...row.childrenIds]);
    }

    private async updateGroupName(groupId: string, name: string) {
        await modellingModule.updateFactorResource(this.factors[groupId]);
    }

    private async updateColumnsName(name: string) {
        await modellingModule.updateFactorResource(this.tableFactorResource);
    }

    private async updateRowsName(name: string) {
        await modellingModule.updateFactorResource(this.tableFactorResource);
    }

    private factorToDisplayValueType(factor: any) {
        if (!factor) {
            return {
                display: "NONE",
                valueType: "NONE",
            };
        }
        return this.VALUE_TYPES.find(
            (v) =>
                factor.valueTypeDefinition &&
                v.valueType == factor.valueTypeDefinition.valueType &&
                v.valueSubType == factor.valueTypeDefinition.valueSubType
        );
    }

    private addRowClick() {
        let groupTypeDefinitions = this.columns
            .map((fg: any) => fg.groupTypeDefinition.valueTypeDefinition)
            .filter((gtd: any) => gtd != null);

        if (this.columns.length === 0 || groupTypeDefinitions.length === 0) {
            this.createForm = {
                type: "ROW",
                categoricalValues: [],
                format: "",
                summaryScoringFunction: "WEIGHTED_SUM",
            };
            this.createDialog = true;
            if (this.createFormRef) {
                this.createFormRef.reset();
                this.createForm.summaryScoringFunction = "WEIGHTED_SUM";
            }
            return;
        }
        this.createDefaultRow();
    }

    // when open the dialog, will not reset the form
    private addColumnClick() {
        let groupTypeDefinitions = this.rows
            .map((fg: any) => fg.groupTypeDefinition.valueTypeDefinition)
            .filter((gtd: any) => gtd != null);
        if (this.rows.length === 0 || groupTypeDefinitions.length === 0) {
            this.createForm = {
                type: "COLUMN",
                categoricalValues: [],
                format: "",
                summaryScoringFunction: "WEIGHTED_SUM",
            };
            console.log(this.createForm);
            this.createDialog = true;
            // if (this.createFormRef) {
            //     this.createFormRef.reset();
            //     this.createForm.summaryScoringFunction = "WEIGHTED_SUM";
            // }
            return;
        }
        this.createDefaultColumn();
    }

    async createDefaultRow() {
        let name = this.getNewName(this.rows, "Row");
        //@ts-ignore
        let factorGroup: FactorGroup = {
            name: name,
            type: "FACTOR_GROUP",
            groupTypeDefinition: {
                type: "TABLE_ROW",
            },
            childrenIds: [],
            summaryScoringFunction: "WEIGHTED_SUM",
        };

        await this.createGroup(factorGroup, "TABLE_ROW", true);
    }

    async create() {
        if (!this.createFormRef.validate()) {
            return;
        }
        if (this.createForm.type === "ROW") {
            await this.createRow();
        } else if (this.createForm.type === "COLUMN") {
            await this.createColumn();
        }
    }

    async createRow() {
        console.log(this.createForm);
        let factorGroup: any = {
            name: this.createForm.name,
            type: "FACTOR_GROUP",
            groupTypeDefinition: {
                type: "TABLE_ROW",
                valueTypeDefinition: {
                    valueType: this.createForm.valueTypeInformation.valueType,
                    valueSubType: this.createForm.valueTypeInformation.valueSubType,
                    valuePrefix: this.createForm.valuePrefix,
                    valueSuffix: this.createForm.valueSuffix,
                    categoricalValues:
                        this.createForm.valueTypeInformation.valueType === "CATEGORICAL"
                            ? _.uniq(this.createForm.categoricalValues)
                            : null,
                    format:
                        this.createForm.valueTypeInformation.valueType === "DATE_TIME" ||
                        this.createForm.valueTypeInformation.valueType === "CATEGORICAL" ||
                        this.createForm.valueTypeInformation.valueType === "TEXT"
                            ? this.createForm.format
                            : null,
                },
            },
            childrenIds: [],
            summaryScoringFunction: this.createForm.summaryScoringFunction || "WEIGHTED_SUM",
        };

        await this.createGroup(factorGroup, "TABLE_ROW");
        this.createFormRef.reset();
        this.createDialog = false;
    }

    async createColumn() {
        //@ts-ignore
        let factorGroup: FactorGroup = {
            name: this.createForm.name,
            type: "FACTOR_GROUP",
            groupTypeDefinition: {
                type: "TABLE_COLUMN",
                valueTypeDefinition: {
                    valueType: this.createForm.valueTypeInformation.valueType,
                    valueSubType: this.createForm.valueTypeInformation.valueSubType,
                    valuePrefix: this.createForm.valuePrefix,
                    valueSuffix: this.createForm.valueSuffix,
                    categoricalValues:
                        this.createForm.valueTypeInformation.valueType === "CATEGORICAL"
                            ? _.uniq(this.createForm.categoricalValues)
                            : null,
                    format:
                        this.createForm.valueTypeInformation.valueType === "DATE_TIME" ||
                        this.createForm.valueTypeInformation.valueType === "CATEGORICAL" ||
                        this.createForm.valueTypeInformation.valueType === "TEXT"
                            ? this.createForm.format
                            : null,
                },
            },
            childrenIds: [],
            summaryScoringFunction: this.createForm.summaryScoringFunction || "WEIGHTED_SUM",
        };
        await this.createGroup(factorGroup, "TABLE_COLUMN");
        this.createFormRef.reset();
        this.createDialog = false;
    }

    // close the dialog box and reset the form
    private cancel() {
        this.createFormRef.reset();
        this.createDialog = false;
    }

    async createDefaultColumn() {
        let name = this.getNewName(this.columns, "Column");
        //@ts-ignore
        let factorGroup: FactorGroup = {
            name: name,
            type: "FACTOR_GROUP",
            groupTypeDefinition: {
                type: "TABLE_COLUMN",
            },
            childrenIds: [],
            summaryScoringFunction: "WEIGHTED_SUM",
        };
        await this.createGroup(factorGroup, "TABLE_COLUMN", true);
    }




    private constructFactorCell(name: string, groupTypeDefinition: any) {
        let newFactor: any = {
            name: name,
            type: "FACTOR",
            numWeightingDots: 1,
        };
        if (groupTypeDefinition && groupTypeDefinition.valueTypeDefinition) {
            newFactor.valueTypeDefinition = {
                valueType: groupTypeDefinition.valueTypeDefinition.valueType,
                valueSubType: groupTypeDefinition.valueTypeDefinition.valueSubType,
                valuePrefix: groupTypeDefinition.valueTypeDefinition.valuePrefix,
                valueSuffix: groupTypeDefinition.valueTypeDefinition.valueSuffix,
                categoricalValues:
                    groupTypeDefinition.valueTypeDefinition.valueType === "CATEGORICAL"
                        ? _.uniq(groupTypeDefinition.valueTypeDefinition.categoricalValues)
                        : null,
                format:
                    groupTypeDefinition.valueTypeDefinition.valueType === "DATE_TIME"
                        ? groupTypeDefinition.valueTypeDefinition.format
                        : null,
            };
        }
        return newFactor;
    }

    async createGroup(
        factorGroup: FactorGroup,
        type: "TABLE_COLUMN" | "TABLE_ROW",
        isDefault?: boolean
    ) {
        this.creating = true;
        NotificationEventBus.emitProcessingMessage();
        await modellingModule.createFactorResource({
            parentId: this.tableFactorResource.id,
            factorResource: factorGroup,
        });
        NotificationEventBus.emitSuccessMessage();
        this.creating = false;
    }


    //Attempts to get the new  name to be the next number in a series if
    // the names are a series of numbers.
    // If not, it will just return defaultValue
    private getNewName(groups: FactorGroup[], defaultValue: string) {
        let numNames = groups
            .map((v: FactorGroup) => v.name || "")
            .map((name: string) => parseInt(name));

        if (numNames.length === 0) return defaultValue;
        if (numNames.length === 1 && !isNaN(numNames[0])) return (numNames[0] + 1).toString();

        let name = null;
        let currentVal = null;
        for (let group of numNames) {
            if (isNaN(group)) return defaultValue;
            if (currentVal == null) {
                currentVal = group;
                continue;
            }
            if (currentVal + 1 === group) {
                currentVal = group;
                name = group + 1;
            } else if (currentVal - 1 === group) {
                currentVal = group;
                name = group - 1;
            } else {
                return defaultValue;
            }
        }
        if (name) {
            return name.toString();
        }
        return defaultValue;
    }

    private scoringDefinitionForm: ScoringDefinitionForm | null = null;
    private showScoringDefinitionEditor = false;

    get scoringKeyDefinitions() {
        return getModule(ScoringKeys).workingModelScoringKeyDefinitions;
    }

    private scoringDefinitionClick(group: FactorGroup) {
        let aFactorId = _.first(group.childrenIds);
        let aFactor = aFactorId ? (this.factors[aFactorId] as Factor) : undefined;
        let groupTypeDefinition = group.groupTypeDefinition.valueTypeDefinition || {};

        let scoringKeyDefinition = aFactor ? this.scoringKeyDefinitions[aFactor.id] : null;
        this.scoringDefinitionForm = {
            id: group.id,
            name: group.name || "",
            inheritedFromName: scoringKeyDefinition
                ? this.getInheritedName(scoringKeyDefinition)
                : null,
            scoringKeys: scoringKeyDefinition ? scoringKeyDefinition.scoringKeys : null,
            valueTypeDefinition: groupTypeDefinition,
            scoringDefinition: aFactor ? aFactor.scoringDefinition : {},
        };
        this.showScoringDefinitionEditor = true;
    }

    private getInheritedName(scoringKeyDefinition: ScoringKeyDefinition) {
        if (!scoringKeyDefinition.inheritedFrom) {
            return;
        }
        let inheritedFrom = scoringKeyDefinition.inheritedFrom;
        let inheritedFromResourceId = scoringKeyDefinition.inheritedFromResourceId;
        if (inheritedFrom === "DECISION" && this.selectedDecision) {
            return `Model (${this.selectedDecision.name})`;
        } else if (inheritedFromResourceId) {
            return `Factor Group (${modellingModule.workingModelFactorResources[inheritedFromResourceId].name})`;
        }
    }

    private async submitScoringDefinition(groupId: string, scoringDefinition: any) {
        this.showScoringDefinitionEditor = false;
        if (!scoringDefinition) return;
        let scoringDefinitionCopy = JSON.parse(JSON.stringify(scoringDefinition));
        this.creating = true;
        let factors = (this.factors[groupId] as FactorGroup).childrenIds.map(
            (f) => this.factors[f]
        );

        factors.forEach((f: any) => {
            Vue.set(f, "scoringDefinition", scoringDefinitionCopy);
        });

        await modellingModule.updateFactorResources(factors);
        this.creating = false;
    }

    private async deleteScoringDefinition(groupId: string) {
        this.showScoringDefinitionEditor = false;

        let factors = (this.factors[groupId] as FactorGroup).childrenIds.map(
            (f) => this.factors[f]
        );

        factors.forEach((f: any) => {
            Vue.delete(f, "scoringDefinition");
        });
        await modellingModule.updateFactorResources(factors);
    }

    // submit summary scoring function to database
    // @param resourceId - resourceId from v-for of elements
    // @param scoringDefinition - summaryScoringFunction choice from child dialog form
    private async submitFactorScoringSum(resourceId, scoringDefinition) {
        if (this.factors[resourceId]) {
            this.factors[resourceId].summaryScoringFunction =
                scoringDefinition.summaryScoringFunction;
            await modellingModule.updateFactorResource(this.factors[resourceId]);
        }
    }
}

interface ScoringDefinitionForm {
    id: string;
    name: string;
    scoringDefinition: any;
    scoringKeys: any[] | null;
    inheritedFromName?: any;
    valueTypeDefinition: ValueTypeDefinition;
}
